
import React, { createContext, useContext, useState, useEffect } from 'react';
import { Product, CartItem, Theme, SiteSettings } from './types';
import { PRODUCTS as INITIAL_PRODUCTS } from './constants';
import { supabase } from './lib/supabase';

interface AppContextType {
  products: Product[];
  setProducts: React.Dispatch<React.SetStateAction<Product[]>>;
  siteSettings: SiteSettings;
  setSiteSettings: React.Dispatch<React.SetStateAction<SiteSettings>>;
  cart: CartItem[];
  wishlist: string[];
  theme: Theme;
  addToCart: (product: Product, selectedVariants?: Record<string, string>) => void;
  removeFromCart: (productId: string, selectedVariants?: Record<string, string>) => void;
  updateQuantity: (productId: string, quantity: number, selectedVariants?: Record<string, string>) => void;
  toggleWishlist: (productId: string) => void;
  toggleTheme: () => void;
  isCartOpen: boolean;
  setIsCartOpen: (open: boolean) => void;
  isLoginOpen: boolean;
  setIsLoginOpen: (open: boolean) => void;
  isAdminMode: boolean;
  setIsAdminMode: (mode: boolean) => void;
  isLoading: boolean;
  refreshData: () => Promise<void>;
}

const DEFAULT_SETTINGS: SiteSettings = {
  siteName: "Do & Get",
  contactEmail: "support@doget.com",
  contactPhone: "+1 (555) 123-4567",
  address: "123 Innovation Drive, Tech City, CA 94103",
  freeShippingThreshold: 99,
  heroSlides: [
    {
      title: "Elegance in Every Detail",
      subtitle: "New Premium Collection 2024",
      description: "Discover the intersection of minimalist design and high-performance functionality.",
      image: "https://picsum.photos/seed/elegan/1920/1080"
    }
  ]
};

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [siteSettings, setSiteSettings] = useState<SiteSettings>(DEFAULT_SETTINGS);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [wishlist, setWishlist] = useState<string[]>([]);
  const [theme, setTheme] = useState<Theme>(Theme.LIGHT);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [productsRes, settingsRes] = await Promise.all([
        supabase.from('products').select('*').order('id', { ascending: true }),
        supabase.from('site_settings').select('*').limit(1).maybeSingle()
      ]);

      if (productsRes.data && productsRes.data.length > 0) {
        setProducts(productsRes.data);
      } else {
        // If DB is empty, use initial local products until seeded
        setProducts(INITIAL_PRODUCTS);
      }

      if (settingsRes.data) {
        setSiteSettings({ ...DEFAULT_SETTINGS, ...settingsRes.data });
      }
    } catch (err) {
      console.error("Supabase fetch failed:", err);
      setProducts(INITIAL_PRODUCTS);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    const savedCart = localStorage.getItem('doget_cart');
    if (savedCart) setCart(JSON.parse(savedCart));
    
    const savedWishlist = localStorage.getItem('doget_wishlist');
    if (savedWishlist) setWishlist(JSON.parse(savedWishlist));
    
    const savedTheme = localStorage.getItem('doget_theme');
    if (savedTheme) setTheme(savedTheme as Theme);
  }, []);

  useEffect(() => {
    localStorage.setItem('doget_cart', JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem('doget_wishlist', JSON.stringify(wishlist));
  }, [wishlist]);

  useEffect(() => {
    localStorage.setItem('doget_theme', theme);
    if (theme === Theme.DARK) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  const addToCart = (product: Product, selectedVariants?: Record<string, string>) => {
    setCart(prev => {
      const existing = prev.find(item => 
        item.id === product.id && 
        JSON.stringify(item.selectedVariants) === JSON.stringify(selectedVariants)
      );
      if (existing) {
        return prev.map(item => 
          (item.id === product.id && JSON.stringify(item.selectedVariants) === JSON.stringify(selectedVariants))
            ? { ...item, quantity: item.quantity + 1 } 
            : item
        );
      }
      return [...prev, { ...product, quantity: 1, selectedVariants }];
    });
    setIsCartOpen(true);
  };

  const removeFromCart = (productId: string, selectedVariants?: Record<string, string>) => {
    setCart(prev => prev.filter(item => 
      !(item.id === productId && JSON.stringify(item.selectedVariants) === JSON.stringify(selectedVariants))
    ));
  };

  const updateQuantity = (productId: string, quantity: number, selectedVariants?: Record<string, string>) => {
    if (quantity <= 0) {
      removeFromCart(productId, selectedVariants);
      return;
    }
    setCart(prev => prev.map(item => 
      (item.id === productId && JSON.stringify(item.selectedVariants) === JSON.stringify(selectedVariants))
        ? { ...item, quantity } 
        : item
    ));
  };

  const toggleWishlist = (productId: string) => {
    setWishlist(prev => prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]);
  };

  const toggleTheme = () => {
    setTheme(prev => prev === Theme.LIGHT ? Theme.DARK : Theme.LIGHT);
  };

  return (
    <AppContext.Provider value={{
      products, setProducts, siteSettings, setSiteSettings,
      cart, wishlist, theme, addToCart, removeFromCart, updateQuantity,
      toggleWishlist, toggleTheme, isCartOpen, setIsCartOpen, isLoginOpen, setIsLoginOpen,
      isAdminMode, setIsAdminMode, isLoading, refreshData: fetchData
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
};
